'use client';

/**
 * CallOrb — the CSS voice-call orb. Replaces the Three.js shader
 * visualizer (voice-visualizer.tsx, deleted): the shader never matched the
 * chat OrbAvatar look, and after the level-engine consolidation removed its
 * outer CSS scale envelope its only response channel was the shader's
 * internal cloud mapping — imperceptible at 128px. This orb IS the
 * OrbAvatar voice gradient (ORB_GRADIENTS.voice — single source of truth),
 * with the level response restored as an explicit scale + glow envelope.
 *
 * Level response uses the established no-React-state-on-the-audio-clock
 * pattern (see primitives/equalizer-bars.tsx): one rAF loop reads
 * `levelRef.current.active` and writes `--orb-scale` / `--orb-glow` CSS
 * vars on the root; the "core" gradient div scales via `transform:
 * scale(var(--orb-scale))` behind a short 80ms linear transition. No extra
 * smoothing here — the engine (useAudioVisualization.ts) already runs the
 * attack/release envelope, and re-smoothing an already-smoothed level is
 * exactly the double-filtering the consolidation removed.
 *
 * ── Turn 13 · per-state layer system (13a–13d) ──────────────────────────
 *
 * Four always-mounted, opacity-cross-faded layer groups sit around the
 * core, one per VizMode, each a direct port of 13a's per-state ring/glow
 * treatment (design-v3.html keyframes: rippleOut, ringIn, glowPulse,
 * glowSoft, shimmerSpin, orbThink):
 *
 *   IDLE     — one ring exhaling out (rippleOut 6s) + soft ambient glow
 *   USER     — two rings drawing INWARD (ringIn 2.1s, staggered 1.05s) +
 *              green aura (cool color = energy coming IN, Sara receiving)
 *   THINKING — a conic-gradient shimmer clipped inside the orb's circle,
 *              rotating (shimmerSpin 5s) while the orb breathes on its own
 *              CSS loop (orbThink) — direction C "Shimmer" per 13c, the
 *              owner-picked recommendation; directions A/B are NOT built
 *   AGENT    — two rings radiating OUTWARD (rippleOut 1.9s, staggered
 *              .95s) + stronger glow + a brighter gradient overlay that
 *              cross-fades in via opacity (gradient STOPS can't be
 *              CSS-transitioned directly — see ORB_GRADIENTS.voiceBright)
 *
 * Layers are NEVER conditionally unmounted on mode change (13b: "rapid mode
 * blips must never pop mid-fade") — only opacity toggles, each mirroring
 * the exact anti-strobe convention call-status-cluster.tsx already
 * established: IDLE/THINKING fade IN delayed 400ms (so a sub-400ms blip on
 * the way back from USER/AGENT never flashes), USER fades OUT delayed
 * 400ms (LiveLevelCue owns that internally elsewhere; here it's inlined
 * since the orb has no separate cue component), AGENT has no delay either
 * direction. This keeps the orb and the status cluster moving together as
 * "one object" per 13b, since they share the same vizMode prop and the same
 * delay values. Per-layer fade DURATIONS approximate 13b's transition table
 * (350ms into thinking, 240ms into Sara, ~400ms back to idle) — deliberately
 * NOT a bespoke choreography engine, just tuned CSS transition durations.
 *
 * THINKING's own scale breath (orbThink) replaces the OLD JS glow LFO this
 * file used to run: previously VizMode.THINKING had level=0 from the engine
 * (it isn't audio-derived), so a synthesized sine LFO gave the orb visual
 * life. Now that life comes from the shimmer + orbThink CSS loop instead, so
 * the rAF loop no longer needs to special-case THINKING at all — it always
 * just writes the real (engine-computed) level, which the engine already
 * zeroes outside USER/AGENT, so --orb-scale naturally rests at 1 under
 * IDLE/THINKING and never fights either CSS keyframe (avoiding the "CSS var
 * fights the keyframe" problem structurally: orbBreathe/orbThink/orbBloom
 * live on a dedicated wrapper around the core, one nesting level UP from the
 * var-driven scale on the core itself — nested transforms compose instead
 * of colliding, the same pattern the old root-breathe/core-var pairing
 * already relied on).
 *
 * AGENT's entry "bloom" (13b: shimmer "blooms" outward, orb pops warm, a
 * single overshoot) is a one-shot keyframe (orb-bloom) replayed by
 * remounting ONLY the small core-motion wrapper via a `key` that increments
 * on each THINKING→AGENT (or anything→AGENT) transition — never on every
 * render, and never touching the always-mounted ambient ring/glow layers
 * above, so those can't pop.
 *
 * ── Size flexibility (13d mobile parity) ─────────────────────────────────
 * All ring/glow geometry below is expressed as an offset from ORB_BASE (the
 * 96px orb turn13.html's mocks are drawn at) scaled by `size / ORB_BASE`, so
 * the exact same layer stack renders correctly at the 128px desktop orb AND
 * the 30px mobile top-bar orb (call-top-bar-status.tsx renders THIS
 * component at size=30 — no copy-pasted layer JSX for mobile).
 *
 * ── Turn 15 · rate-limit waiting UX (15d/15e) ───────────────────────────
 *
 * Two additions, both orthogonal to the per-mode layer system above:
 *
 *   `dimmed` — EXHAUSTED (retry budget ran out, useCallMode's modelBusy ===
 *   'exhausted'): the orb "dims and rests" regardless of `mode` — opacity
 *   .5 + saturate(.75) on the whole orb, and every mode layer's
 *   ring/shimmer/breathe is force-hidden (ANDed with `!dimmed`) rather than
 *   left to whatever `mode` happens to say underneath. Manual "Ask again" is
 *   the only way out, so this deliberately doesn't derive from the level
 *   engine's mode signal the way every other layer here does.
 *
 *   `releaseSignal` — bumped once per MODEL_BUSY recovery ("the exhale",
 *   15e): replays a single one-shot expanding ring LAYERED ON TOP of the
 *   normal AGENT-entry bloom, via the exact same key-remount trick the
 *   bloom itself uses (see `bloomKey` below) — extending that Turn-13
 *   mechanism rather than duplicating it. 0/undefined (the default) plays
 *   nothing; only a genuine CHANGE in the signal replays the ripple.
 *
 * ── Turn 16 · optimistic call-start (16a/16b/16c) ───────────────────────
 *
 *   `connecting` — true for the WHOLE `connecting` call-phase (both the
 *   plain dial and the waiting-on-mic sub-state share this one orb
 *   treatment; only the notice card differs, see call-start-notice.tsx):
 *   three dial rings (call-dial-ring, staggered 0/1.2s/2.4s) + a soft halo
 *   (call-glow) fade up around the orb, which itself holds a calm
 *   `call-pulse` breath instead of `orb-breathe`. Deliberately distinct from
 *   AGENT's fast overlapping rings (see the file header's Turn 16 section) —
 *   never shimmer, never the fast 1.9s cadence.
 *
 *   `reconnecting` — true only for the calm `failed(ws_closed)` variant
 *   (16c's "still working on its own" register, not a hard failure): a
 *   single, quieter ring (no glow layer) and the orb itself settles to
 *   opacity .7 — visually calmer than `connecting`'s full three-ring dial,
 *   and deliberately NOT `dimmed`'s "stopped, rest" desaturation.
 *
 *   The "she picked up" moment (16b) — connecting flipping true→false while
 *   landing on a genuinely LIVE call (not a failure) — replays the EXACT
 *   SAME one-shot bloom + release ripple 15e already built, verbatim, by
 *   extending the bloomKey/rippleKey effects below rather than adding a new
 *   mechanism (see the effect's own comment).
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { VizMode } from '@/types/chat';
import { ORB_GRADIENTS } from '@/lib/design-tokens';
import type { AudioLevels } from '@/hooks/useAudioVisualization';

/** Scale envelope: 1 → 1.07 across the engine's 0..1 level — perceptible
 *  speech response without turning the orb cartoonish. */
const SCALE_GAIN = 0.07;

/** Reference diameter turn13.html's 13a mocks are drawn at — ring widths,
 *  glow insets, and border widths below are fractions of this, multiplied
 *  by `size / ORB_BASE` at render time (see file header, "Size flexibility"). */
const ORB_BASE = 96;

/** [Turn 16, 16b] Must match orb-bloom's declared duration in
 *  tailwind.config.ts (`orb-bloom .32s ease-out`) — the window
 *  `pickupBloomActive` force-applies the bloom class for, see that state's
 *  declaration below. */
const PICKUP_BLOOM_MS = 320;

// Ring/glow colors (13a) — used only inside this file (both the desktop and
// mobile call sites render THIS component, so there's exactly one source
// literal per color despite two visual call sites; doesn't meet the
// "2+ places in source" bar for design-tokens.ts).
const IDLE_RING = 'rgba(236,95,28,.28)';
const IDLE_GLOW = 'rgba(255,150,70,.24)';
const THINK_GLOW = 'rgba(255,150,70,.22)';
const USER_RING = 'rgba(31,138,91,.5)';
const USER_RING_SOFT = 'rgba(31,138,91,.4)';
const USER_GLOW = 'rgba(31,138,91,.22)';
const AGENT_RING = 'rgba(236,95,28,.5)';
const AGENT_RING_SOFT = 'rgba(236,95,28,.4)';
const AGENT_GLOW = 'rgba(255,150,70,.4)';
const SHIMMER_CONIC =
  'conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,.55) 42deg, transparent 96deg, transparent 210deg, rgba(255,242,222,.4) 256deg, transparent 316deg)';

// Turn 16 (16a/16c) — dial ring/glow colors, same "local, single call site"
// rule as the block above.
const DIAL_RING = 'rgba(236,95,28,.32)';
const DIAL_GLOW = 'rgba(255,150,70,.2)';
/** The calm reconnecting variant's single ring — quieter than DIAL_RING. */
const RECONNECT_RING = 'rgba(236,95,28,.28)';

export interface CallOrbProps {
  /** Diameter in px. */
  size?: number;
  mode: VizMode;
  /** Engine ref from useAudioVisualization — `.active` read per frame. */
  levelRef: React.RefObject<AudioLevels>;
  className?: string;
  /** Turn 15 (15d) — true while a rate-limit retry budget is EXHAUSTED. See
   *  file header's "Turn 15" section. */
  dimmed?: boolean;
  /** Turn 15 (15e) — bumped once per MODEL_BUSY recovery. See file header. */
  releaseSignal?: number;
  /** [Turn 16] True for the whole `connecting` call-phase — three dial
   *  rings + glow + a calm pulse. See file header's Turn 16 section. */
  connecting?: boolean;
  /** [Turn 16] True only for the calm `failed(ws_closed)` variant — a
   *  single quiet ring + opacity .7, no glow. See file header. */
  reconnecting?: boolean;
}

export function CallOrb({
  size = 128,
  mode,
  levelRef,
  className,
  dimmed = false,
  releaseSignal,
  connecting = false,
  reconnecting = false,
}: CallOrbProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const s = size / ORB_BASE;
  // Minimum border width so ring strokes don't anti-alias away to nothing
  // at the 30px mobile size (2px * 30/96 ≈ 0.6px).
  const ringBorder = Math.max(1, 2 * s);
  const idleRingBorder = Math.max(1, 1.5 * s);
  // Release ripple (15e) shares the idle ring's 1.5px-at-96px base — kept as
  // its own name since it backs a conceptually distinct one-shot element.
  const releaseRingBorder = idleRingBorder;

  // `dimmed` (EXHAUSTED, 15d) force-hides every mode layer below regardless
  // of what `mode` says — see file header. [Turn 16] `connecting`/
  // `reconnecting` are ALSO ANDed with `!dimmed` for the same reason: a
  // failed(mic_denied/timeout/backend/network) call is `dimmed`, and must
  // win over a stale `connecting`/`reconnecting` prop the caller hasn't
  // cleared yet. The four mode layers are additionally gated on
  // `!connecting && !reconnecting` — in practice `mode` is still IDLE while
  // connecting (the level engine has nothing to report yet), and without
  // this, IDLE's own ripple/breathe classes would render ALONGSIDE the new
  // dial-ring/call-pulse layers below — two ambient rings and two
  // conflicting `animation` values on the same core element.
  const isConnecting = !dimmed && connecting;
  const isReconnecting = !dimmed && !connecting && reconnecting;
  const isConnectingFamily = isConnecting || isReconnecting;
  const isIdle = !dimmed && !isConnectingFamily && mode === VizMode.IDLE;
  const isUser = !dimmed && !isConnectingFamily && mode === VizMode.USER;
  const isThinking =
    !dimmed && !isConnectingFamily && mode === VizMode.THINKING;
  const isAgent = !dimmed && !isConnectingFamily && mode === VizMode.AGENT;

  // AGENT-entry "bloom" (13b) — see file header. Only increments on a FRESH
  // transition into AGENT, never on every render (this isn't per-frame
  // audio-clock state; it changes once per mode transition, same cadence as
  // vizMode itself).
  const [bloomKey, setBloomKey] = useState(0);
  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (mode === VizMode.AGENT && prevModeRef.current !== VizMode.AGENT) {
      setBloomKey((k) => k + 1);
    }
    prevModeRef.current = mode;
  }, [mode]);

  // Release ripple (15e) — a SEPARATE one-shot, keyed off the caller's own
  // signal rather than a mode transition the way bloomKey is (the caller
  // already knows exactly when a recovery happened; re-deriving that from
  // `mode` here would mean firing on EVERY THINKING→AGENT entry, not just a
  // MODEL_BUSY recovery). Starts at 0 so nothing plays on initial mount —
  // only a genuine change increments it.
  const [rippleKey, setRippleKey] = useState(0);
  const prevReleaseSignalRef = useRef(releaseSignal);
  useEffect(() => {
    if (
      releaseSignal !== undefined &&
      releaseSignal !== prevReleaseSignalRef.current
    ) {
      setRippleKey((k) => k + 1);
    }
    prevReleaseSignalRef.current = releaseSignal;
  }, [releaseSignal]);

  // [Turn 16, 16b] "She picked up" — connecting flipping true→false while
  // landing on a genuinely LIVE call (not a failure) replays the EXACT SAME
  // one-shot AGENT bloom + 15e release ripple this file already has,
  // extending bloomKey/rippleKey's own mechanisms rather than adding new
  // ones. Guarded on `!dimmed && !reconnecting` in the POST-transition
  // state, so a connecting→failed (either amber or the calm ws_closed
  // variant) — also a connecting true→false edge — does NOT play the
  // celebratory bloom/ripple.
  //
  // `pickupBloomActive` exists because bloomKey ALONE only remounts the core
  // wrapper below — which animation class actually shows still depends on
  // `mode`, and at this exact moment `mode` is realistically still IDLE
  // ("Ready to listen" hasn't triggered Sara speaking yet), not AGENT. This
  // flag force-applies orb-bloom regardless of mode for exactly one
  // PICKUP_BLOOM_MS window (orb-bloom's own declared duration — see
  // tailwind.config.ts), suppressing the mode-driven breathe/think/call-pulse
  // classes for that window so they can't collide with it on the same
  // element (two `animation` values on one element is a real, silent bug —
  // last-in-stylesheet wins unpredictably).
  const [pickupBloomActive, setPickupBloomActive] = useState(false);
  const prevConnectingRef = useRef(connecting);
  useEffect(() => {
    if (prevConnectingRef.current && !connecting && !dimmed && !reconnecting) {
      setBloomKey((k) => k + 1);
      setRippleKey((k) => k + 1);
      setPickupBloomActive(true);
    }
    prevConnectingRef.current = connecting;
  }, [connecting, dimmed, reconnecting]);

  useEffect(() => {
    if (!pickupBloomActive) return;
    const timer = setTimeout(
      () => setPickupBloomActive(false),
      PICKUP_BLOOM_MS
    );
    return () => clearTimeout(timer);
  }, [pickupBloomActive]);

  useEffect(() => {
    // Checked once per mount: reduced-motion users get a static orb (scale
    // clamped to 1); the glow still tracks the level — an opacity shift,
    // not motion.
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    let raf = 0;
    const tick = () => {
      // No mode branching needed: the engine (useAudioVisualization.ts)
      // already zeroes `.active` outside USER/AGENT, so this naturally
      // rests at scale 1 / glow 0 under IDLE and THINKING — leaving those
      // two CSS keyframes (orb-breathe, orb-think) as the only source of
      // motion there, nothing to fight.
      const level = Math.max(0, Math.min(1, levelRef.current?.active ?? 0));
      const el = rootRef.current;
      if (el) {
        el.style.setProperty(
          '--orb-scale',
          reduceMotion ? '1' : (1 + SCALE_GAIN * level).toFixed(4)
        );
        el.style.setProperty('--orb-glow', level.toFixed(3));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [levelRef]);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className={cn(
        'relative flex shrink-0 items-center justify-center transition-[opacity,filter] duration-[300ms]',
        // EXHAUSTED "dim and rest" (15d) — opacity + saturate on the WHOLE
        // orb, on top of the per-layer `!dimmed` hiding above. [Turn 16]
        // `reconnecting` (calm ws_closed) settles to a quieter .7 opacity
        // instead — never desaturated, this isn't "stopped, resting".
        // `dimmed` still wins if somehow both are set (see isReconnecting).
        dimmed
          ? 'opacity-[.5] saturate-[.75]'
          : isReconnecting && 'opacity-[.7]',
        className
      )}
      style={{ width: size, height: size }}
    >
      {/* ── IDLE: one exhaling ring + soft ambient glow (13a) ── */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 transition-opacity',
          isIdle
            ? 'opacity-[1] duration-[400ms] delay-[400ms]'
            : 'opacity-[0] duration-200'
        )}
      >
        {/* Rings carry a STATIC centering transform in addition to the one
            inside the keyframes (the mock does the same): while a ring's
            animation isn't applying — reduced motion, or a stagger delay
            before the first iteration — the keyframe contributes nothing,
            and without the static transform the ring would sit with its
            top-left corner at the orb's center. They're also fully hidden
            under reduced motion (13b: "ripple ... dropped"); the glow
            layers stay, being opacity-only. */}
        <span
          className="absolute left-1/2 top-1/2 rounded-full motion-safe:animate-ripple-out-idle motion-reduce:hidden"
          style={{
            width: size,
            height: size,
            border: `${idleRingBorder}px solid ${IDLE_RING}`,
            transform: 'translate(-50%,-50%)',
          }}
        />
        <span
          className="absolute rounded-full animate-glow-soft-idle"
          style={{
            inset: -16 * s,
            background: `radial-gradient(circle, ${IDLE_GLOW}, transparent 68%)`,
          }}
        />
      </div>

      {/* ── USER: two inward rings + green aura (13a) — cool color, energy
          drawing IN (Sara receiving). Fade-OUT is the delayed side here
          (mirrors LiveLevelCue's own anti-strobe fade-out), matching the
          established "active cue holds 400ms" convention (13b). ── */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 transition-opacity',
          isUser
            ? 'opacity-[1] duration-[300ms]'
            : 'opacity-[0] duration-[300ms] delay-[400ms]'
        )}
      >
        <span
          className="absolute left-1/2 top-1/2 rounded-full motion-safe:animate-ring-in motion-reduce:hidden"
          style={{
            width: 118 * s,
            height: 118 * s,
            border: `${ringBorder}px solid ${USER_RING}`,
            transform: 'translate(-50%,-50%)',
          }}
        />
        <span
          className="absolute left-1/2 top-1/2 rounded-full motion-safe:animate-ring-in motion-reduce:hidden"
          style={{
            width: 118 * s,
            height: 118 * s,
            border: `${ringBorder}px solid ${USER_RING_SOFT}`,
            animationDelay: '1.05s',
            transform: 'translate(-50%,-50%)',
          }}
        />
        <span
          className="absolute rounded-full animate-glow-pulse-user"
          style={{
            inset: -18 * s,
            background: `radial-gradient(circle, ${USER_GLOW}, transparent 66%)`,
          }}
        />
      </div>

      {/* ── THINKING: shimmer clipped to the orb + soft glow (13a/13c-C) ── */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 transition-opacity',
          isThinking
            ? 'opacity-[1] duration-[350ms] delay-[400ms]'
            : 'opacity-[0] duration-200'
        )}
      >
        {/* Whole shimmer hidden (not just paused) under reduced motion —
            13b drops shimmer entirely; a frozen sheen would read as a
            permanent highlight smear on the orb. */}
        <div className="absolute inset-0 overflow-hidden rounded-full motion-reduce:hidden">
          <span
            className="absolute motion-safe:animate-shimmer-spin"
            style={{ inset: '-30%', background: SHIMMER_CONIC }}
          />
        </div>
        <span
          className="absolute rounded-full animate-glow-soft-think"
          style={{
            inset: -14 * s,
            background: `radial-gradient(circle, ${THINK_GLOW}, transparent 68%)`,
          }}
        />
      </div>

      {/* ── AGENT: two outward rings + stronger glow (13a) — warm color,
          energy radiating OUT (Sara emitting). No delay either direction,
          matching call-status-cluster.tsx's AGENT bars. ── */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 transition-opacity duration-[240ms]',
          isAgent ? 'opacity-[1]' : 'opacity-[0]'
        )}
      >
        <span
          className="absolute left-1/2 top-1/2 rounded-full motion-safe:animate-ripple-out-agent motion-reduce:hidden"
          style={{
            width: size,
            height: size,
            border: `${ringBorder}px solid ${AGENT_RING}`,
            transform: 'translate(-50%,-50%)',
          }}
        />
        <span
          className="absolute left-1/2 top-1/2 rounded-full motion-safe:animate-ripple-out-agent motion-reduce:hidden"
          style={{
            width: size,
            height: size,
            border: `${ringBorder}px solid ${AGENT_RING_SOFT}`,
            animationDelay: '.95s',
            transform: 'translate(-50%,-50%)',
          }}
        />
        <span
          className="absolute rounded-full animate-glow-pulse-agent"
          style={{
            inset: -18 * s,
            background: `radial-gradient(circle, ${AGENT_GLOW}, transparent 66%)`,
          }}
        />
      </div>

      {/* ── [Turn 16] CONNECTING/RECONNECTING: dial ring(s) + glow (16a/16c) —
          desaturated, one-ring-at-a-time, fading up from zero; deliberately
          NOT AGENT's fast overlapping pair (see file header). No anti-strobe
          delay either direction — a deliberate driver-decided phase, not a
          blippy engine signal (same reasoning as EXHAUSTED/statusOverride
          elsewhere in this tree). `connecting` gets the full three-ring dial
          (+220ms entrance stagger on top of the 1.2s/2.4s cadence — 16e:
          "the reach visibly starts, wasn't mid-flight on arrival");
          `reconnecting` (calm ws_closed) gets a single quieter ring, no
          glow. ── */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 transition-opacity duration-200',
          isConnectingFamily ? 'opacity-[1]' : 'opacity-[0]'
        )}
      >
        {isReconnecting ? (
          <span
            className="absolute left-1/2 top-1/2 rounded-full opacity-[0] motion-safe:animate-call-dial-ring motion-reduce:hidden"
            style={{
              width: size,
              height: size,
              border: `${idleRingBorder}px solid ${RECONNECT_RING}`,
              transform: 'translate(-50%,-50%)',
            }}
          />
        ) : (
          <>
            <span
              className="absolute left-1/2 top-1/2 rounded-full opacity-[0] motion-safe:animate-call-dial-ring motion-reduce:hidden"
              style={{
                width: size,
                height: size,
                border: `${idleRingBorder}px solid ${DIAL_RING}`,
                animationDelay: '220ms',
                transform: 'translate(-50%,-50%)',
              }}
            />
            <span
              className="absolute left-1/2 top-1/2 rounded-full opacity-[0] motion-safe:animate-call-dial-ring motion-reduce:hidden"
              style={{
                width: size,
                height: size,
                border: `${idleRingBorder}px solid ${DIAL_RING}`,
                animationDelay: '1.42s',
                transform: 'translate(-50%,-50%)',
              }}
            />
            <span
              className="absolute left-1/2 top-1/2 rounded-full opacity-[0] motion-safe:animate-call-dial-ring motion-reduce:hidden"
              style={{
                width: size,
                height: size,
                border: `${idleRingBorder}px solid ${DIAL_RING}`,
                animationDelay: '2.62s',
                transform: 'translate(-50%,-50%)',
              }}
            />
            <span
              className="absolute rounded-full opacity-[.4] motion-safe:animate-call-glow"
              style={{
                inset: -14 * s,
                background: `radial-gradient(circle, ${DIAL_GLOW}, transparent 68%)`,
              }}
            />
          </>
        )}
      </div>

      {/* Release ripple (15e "the exhale") — a single one-shot expanding
          ring layered ON TOP of the AGENT bloom above, not a replacement
          for it. Remounted via `key` exactly like bloomKey (see file
          header); rendered only once a signal has actually fired, so
          nothing plays on initial mount. Reduced motion drops it entirely
          (15e: "no bloom/ripple"), same convention as every other ring. */}
      {rippleKey > 0 && (
        <span
          // Namespaced — bloomKey's remount div below is a SIBLING under the
          // same root, and both counters start at 1; bare numeric keys
          // collide the first time a recovery and a bloom coexist (React
          // duplicate-key error, caught live).
          key={`ripple-${rippleKey}`}
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 rounded-full motion-safe:animate-orb-release-ripple motion-reduce:hidden"
          style={{
            width: size,
            height: size,
            border: `${releaseRingBorder}px solid rgba(236,95,28,.5)`,
            transform: 'translate(-50%,-50%)',
          }}
        />
      )}

      {/* Warm halo (real-level opacity) — REAL level-reactivity stays the
          glow driver during USER/AGENT (the ambient glow-pulse layers above
          are fixed-period ambient loops, not level-tied — same convention
          as the orb's own scale, see file header). */}
      <div
        className="absolute rounded-full transition-opacity duration-[80ms] ease-linear"
        style={{
          inset: -16 * s,
          background:
            'radial-gradient(circle, rgba(255,150,70,.26) 0%, transparent 68%)',
          opacity: 'calc(.55 + .45 * var(--orb-glow, 0))',
        }}
      />

      {/* Core motion wrapper — owns the mode-gated CSS keyframe (breathe /
          think / bloom); remounted via `key` only on a fresh AGENT entry so
          the one-shot bloom replays (see file header). Nested INSIDE this,
          the gradient core's own `scale(var(--orb-scale))` composes rather
          than fights, since each transform lives on a different element. */}
      <div
        key={bloomKey}
        className={cn(
          'relative size-full',
          // [Turn 16, 16b] `pickupBloomActive` force-applies the bloom and
          // SUPPRESSES every mode-driven class below for that one window —
          // see the state's own declaration comment for why (a stray second
          // `animation` on this element is a silent, unpredictable bug).
          (isAgent || pickupBloomActive) && 'motion-safe:animate-orb-bloom',
          !pickupBloomActive && isIdle && 'motion-safe:animate-orb-breathe',
          !pickupBloomActive && isThinking && 'motion-safe:animate-orb-think',
          // A calm, steady pulse — NEVER orb-breathe's idle loop or
          // orb-think's shimmer-paired breath — for both connecting/
          // reconnecting (see file header's distinctness contract).
          !pickupBloomActive &&
            isConnectingFamily &&
            'motion-safe:animate-call-pulse'
        )}
      >
        {/* The orb core — exactly the chat OrbAvatar voice gradient. 80ms
            linear transition only bridges frames; the engine already
            smooths the level (do NOT add another smoother here). */}
        <div
          className="absolute inset-0 rounded-full transition-transform duration-[80ms] ease-linear"
          style={{
            background: ORB_GRADIENTS.voice,
            transform: 'scale(var(--orb-scale, 1))',
            boxShadow: '0 20px 44px -14px rgba(236,95,28,.55)',
          }}
        />
        {/* AGENT brighter overlay (13a) — cross-fades in via opacity since
            gradient stops can't transition directly. */}
        <div
          className={cn(
            'absolute inset-0 rounded-full transition-opacity duration-[240ms]',
            isAgent ? 'opacity-[1]' : 'opacity-[0]'
          )}
          style={{ background: ORB_GRADIENTS.voiceBright }}
        />
      </div>
    </div>
  );
}
